import {
  IonButton,
  IonContent,
  IonInput,
  IonLoading,
  IonModal
} from '@ionic/react';
import React, { useCallback, useEffect, useState } from 'react';

import {
  ButtonContainer,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { useDentist } from '@/contexts';
import { useToast } from '@/hooks';
import { formatName } from '@/utils/helpers/formatName';
import { arrowBackOutline, checkmarkCircleOutline } from 'ionicons/icons';

import './UpdateName.css';

interface UpdateNameProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

const UpdateName: React.FC<UpdateNameProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const displayToast = useToast();
  const { selectedDentist: dentist, updateDentistMutation } = useDentist();

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [firstNameError, setFirstNameError] = useState<boolean>(false);
  const [lastNameError, setLastNameError] = useState<boolean>(false);

  useEffect(() => {
    if (dentist) {
      setFirstName(dentist.first_name);
      setLastName(dentist.last_name);
    }
  }, [dentist?.first_name, dentist?.last_name]);

  const copy = {
    subHeader: 'DENTIST NAME',
    header: (
      <>
        Edit <Text color="primary">name</Text>
      </>
    ),
    message: 'Please enter your first name and last name below.',
    buttonText: 'Update',
    firstNameLabel: 'First Name',
    firstNamePlaceholder: 'First Name',
    lastNameLabel: 'Last Name',
    lastNamePlaceholder: 'Last Name'
  };

  const onNext = useCallback(async () => {
    if (!firstName) {
      setFirstNameError(true);
      displayToast({
        message: 'Please enter your first name.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
      return;
    }

    if (!lastName) {
      setLastNameError(true);
      displayToast({
        message: 'Please enter your last name.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
      return;
    }

    const formattedFirstName = formatName(firstName);
    const formattedLastName = formatName(lastName);

    if (dentist?.id) {
      await updateDentistMutation.mutateAsync({
        dentistId: dentist.id,
        dentist: {
          ...dentist,
          first_name: formattedFirstName,
          last_name: formattedLastName
        }
      });
      displayToast({
        message: 'Dentist name updated successfully.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
      onClose();
    }
  }, [firstName, lastName, dentist, updateDentistMutation, onClose]);

  const onCloseModal = useCallback(() => {
    setFirstNameError(false);
    setLastNameError(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    return () => {
      setFirstNameError(false);
      setLastNameError(false);
    };
  }, []);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onCloseModal}
      presentingElement={presentingElement}
    >
      <IonContent>
        <Padding withSafeArea={!presentingElement}>
          <div className="update-name">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={onClose}
            />
            <Spacer top={16} bottom={24}>
              <h4 className="update-name__sub-header">{copy.subHeader}</h4>
              <h1 className="update-name__header">{copy.header}</h1>
              <p className="update-name__message">{copy.message}</p>
            </Spacer>

            <Spacer bottom={124}>
              <div className="update-name__form">
                <div
                  className={`update-name__input ${firstNameError ? 'update-name__input--error' : ''}`}
                >
                  <IonInput
                    label={copy.firstNameLabel}
                    labelPlacement="stacked"
                    placeholder={copy.firstNamePlaceholder}
                    value={firstName}
                    onIonInput={(e: CustomEvent) =>
                      setFirstName(e.detail.value)
                    }
                  />
                </div>
                <div
                  className={`update-name__input ${lastNameError ? 'update-name__input--error' : ''}`}
                >
                  <IonInput
                    label={copy.lastNameLabel}
                    labelPlacement="stacked"
                    placeholder={copy.lastNamePlaceholder}
                    value={lastName}
                    onIonInput={(e: CustomEvent) => setLastName(e.detail.value)}
                  />
                </div>
              </div>
            </Spacer>

            <ButtonContainer>
              <IonButton
                id="next-button"
                onClick={onNext}
                expand="block"
                fill="solid"
                size="default"
                color="primary"
                strong={true}
                disabled={updateDentistMutation.isPending}
              >
                <Icon slot="start" icon={checkmarkCircleOutline} />
                {copy.buttonText}
              </IonButton>
            </ButtonContainer>
          </div>
        </Padding>
        <IonLoading
          isOpen={updateDentistMutation.isPending}
          message="Loading..."
          spinner="crescent"
        />
      </IonContent>
    </IonModal>
  );
};

export default UpdateName;
